import React, { useState } from 'react'
import FilterMenu from '../FilterMenu'
import { Autocomplete, Box, IconButton, Stack, TextField, Typography } from '@mui/material'
import { WorkEntry } from '../../../store/types'
import { getDurationString, getHighestWorkTime, getLowestWorkTime } from '../../../store/utils/worklogUtils'
import CollapsibleSection from '../../Button/CollapsibleSection'
import { t } from 'i18next'
import { Trans } from 'react-i18next'
import { Close } from '@mui/icons-material'
import { StyledSlider } from '../../../styles/SliderStyles'
import { useLeftPadding } from '../../../store/redux/hooks'

type FilterEntriesMenuProps = {
  entries: WorkEntry[]
  setFilteringProps: React.Dispatch<
    React.SetStateAction<{
      projects: string[]
      timeFrom: number
      timeTo: number
    }>
  >
}

const FilterEntriesMenu = ({ entries, setFilteringProps }: FilterEntriesMenuProps) => {
  const leftPadding = useLeftPadding()
  const projects = [...new Map(entries.filter(Boolean).map(e => [e['project']['id'], e])).values()]
  const projectsIds = [...new Set(entries.map(e => e.project.id))]
  const [selectedProjects, setSelectedProjects] = useState<WorkEntry[]>([])

  const min = entries.length ? 0 : getLowestWorkTime(entries)
  const max = entries.length === 0 ? 24 : getHighestWorkTime(entries)
  const minDistance = 0.5

  const [rangeValue, setRangeValue] = React.useState<number[]>([min, max])

  const marks = [
    {
      value: min,
      label: getDurationString(min),
    },
    {
      value: max,
      label: getDurationString(max),
    },
  ]

  const handleRangeValueChange = (event: Event, newValue: number | number[], activeThumb: number) => {
    if (!Array.isArray(newValue)) {
      return
    }

    if (newValue[1] - newValue[0] < minDistance) {
      if (activeThumb === 0) {
        const clamped = Math.min(newValue[0], max - minDistance)
        setRangeValue([clamped, clamped + minDistance])
      } else {
        const clamped = Math.max(newValue[1], min + minDistance)
        setRangeValue([clamped - minDistance, clamped])
      }
    } else {
      setRangeValue(newValue as number[])
    }
  }

  const handleRemoveProject = (entry: WorkEntry) => {
    setSelectedProjects(prev => prev.filter(e => e.project.id !== entry.project.id))
  }

  const handleClear = () => {
    setFilteringProps({
      projects: projectsIds,
      timeFrom: min,
      timeTo: max,
    })
    setRangeValue([min, max])
    setSelectedProjects([])
  }

  const handleFilter = () => {
    setFilteringProps({
      projects: selectedProjects.length === 0 ? projectsIds : selectedProjects.map(p => p.project.id),
      timeFrom: rangeValue[0],
      timeTo: rangeValue[1],
    })
  }
  return (
    <FilterMenu clear={handleClear} filter={handleFilter} isLeftPadding={true}>
      <CollapsibleSection
        text={t('page.worklog.filter.projects')}
        info={
          selectedProjects.length !== 0 ? (
            <Trans i18nKey="page.worklog.filter.selectedCount" values={{ count: selectedProjects.length }} />
          ) : (
            ''
          )
        }
      >
        <Box sx={{ py: 1, pl: leftPadding }}>
          <Autocomplete
            id="size-small-outlined"
            size="small"
            options={projects}
            getOptionLabel={(p: WorkEntry) => p.project.name}
            renderTags={() => null}
            multiple={true}
            value={selectedProjects}
            onChange={(_event, newValue) => {
              setSelectedProjects(newValue)
            }}
            disableCloseOnSelect={true}
            noOptionsText={t('page.worklog.filter.projectsNotFound')}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                label={t('page.worklog.filter.searchProjects')}
                placeholder={t('page.worklog.filter.enterProjectName')}
                fullWidth
                sx={{ mt: 1 }}
              />
            )}
          />

          <Stack sx={{ gap: 1, pl: 1, pr: 0.5, pt: 2 }}>
            {selectedProjects.length !== 0 && (
              <Typography variant="body1" fontWeight={600} color={'text.base'} pb={0.5}>
                {t('page.worklog.filter.selected')}
              </Typography>
            )}

            {selectedProjects.map(p => (
              <Stack key={p.project.id} direction="row" justifyContent="space-between">
                <Typography variant="body2">{p.project.name}</Typography>
                <IconButton size="small" onClick={() => handleRemoveProject(p)}>
                  <Close sx={{ width: 14, height: 14 }} />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </Box>
      </CollapsibleSection>

      <Stack>
        <Typography sx={{ pl: leftPadding }} variant="h6" component="span" color="text.mediumEmphasis">
          {t('page.worklog.filter.timeSpent')}
        </Typography>
        <Stack sx={{ pr: '10.5px', pl: `calc(${leftPadding} + 10.5px)` }}>
          <StyledSlider
            value={rangeValue}
            onChange={handleRangeValueChange}
            color="primary"
            valueLabelFormat={x => getDurationString(x)}
            min={min}
            max={max}
            step={0.5}
            disableSwap
            valueLabelDisplay="auto"
            marks={marks}
          />
        </Stack>
      </Stack>
    </FilterMenu>
  )
}

export default FilterEntriesMenu
